import { Box } from '@mui/material'
import { useEffect, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { getSubscriptionStatus } from '../../services/subscriptionService'
import { sessionState } from '../../state'

import PaymentDueBanner from './PaymentDueBanner'

/**
 * PaymentDueBannerWrapper Component
 *
 * Wrapper component that fetches subscription status and conditionally renders
 * the PaymentDueBanner when an office is in grace period.
 *
 * This component:
 * - Checks if user has a selected office
 * - Fetches subscription status for the office
 * - Determines if user is the office owner
 * - Renders PaymentDueBanner only when in grace period
 */
export const PaymentDueBannerWrapper = () => {
  const session = useRecoilValue(sessionState)
  const [showBanner, setShowBanner] = useState(false)
  const [gracePeriodEnd, setGracePeriodEnd] = useState<Date | null>(null)
  const [ownerEmail, setOwnerEmail] = useState<string | undefined>(undefined)

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      // Only check if user is authenticated and has a selected office
      if (!session.user || !session.selectedOffice) {
        setShowBanner(false)
        return
      }

      try {
        const status = await getSubscriptionStatus(session.selectedOffice.oid!)

        // Show banner if in grace period
        if (status.gracePeriodEnd && status.gracePeriodDaysRemaining) {
          setShowBanner(true)
          setGracePeriodEnd(status.gracePeriodEnd)

          // Get owner email if current user is not the owner
          if (session.selectedOffice.uid !== session.user.uid) {
            // Find owner in offices list or fetch from database
            const owner = session.offices?.find(
              o => o.oid === session.selectedOffice?.oid
            )
            if (owner) {
              // We need to get the owner's email - it's stored in the user record
              // For now, we'll leave it undefined and the banner will handle it
              setOwnerEmail(undefined)
            }
          }
        } else {
          setShowBanner(false)
        }
      } catch (error) {
        console.error('Error checking subscription status:', error)
        setShowBanner(false)
      }
    }

    checkSubscriptionStatus()

    // Re-check every 5 minutes
    const interval = setInterval(checkSubscriptionStatus, 5 * 60 * 1000)

    return () => clearInterval(interval)
  }, [session.user, session.selectedOffice, session.offices])

  // Don't render anything if banner shouldn't be shown
  if (
    !showBanner ||
    !gracePeriodEnd ||
    !session.selectedOffice ||
    !session.user
  ) {
    return null
  }

  const isOwner = session.selectedOffice.uid === session.user.uid

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <PaymentDueBanner
        officeId={session.selectedOffice.oid!}
        gracePeriodEnd={gracePeriodEnd}
        isOwner={isOwner}
        ownerEmail={ownerEmail}
      />
    </Box>
  )
}

export default PaymentDueBannerWrapper
